import React, { useMemo } from 'react';
import { get } from 'lodash';
import { useAtomValue } from 'jotai';
import { RiseOutlined, FallOutlined } from '@ant-design/icons';
import humanizeUtil from '@src/utils/humanizeUtil';
import { getMetricDisplayName, getPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';
import BaseChart, { useChartInstance } from '@src/components/common/BaseChart';
import { formatMetricDataForChart } from '../../MetricPanel/components/utils/utils';
import {
  Row,
  FullCell,
  Cell,
  CellNumber,
  CellPrimary,
  SparklineWrapper,
  TrendValue,
  CellWrapper,
} from './styled';
import { enableDodAtom } from '../../atoms';

interface SparklineProps {
  metricKey: string;
  chartData: any[];
  dodData: any[];
}

const Sparkline = ({ metricKey, chartData, dodData }: SparklineProps) => {
  const { instance, chartInstanceRefCb } = useChartInstance({});
  const dodEnabled = useAtomValue(enableDodAtom);

  const chartOption = useMemo(
    () =>
      formatMetricDataForChart({
        chartData,
        metricKey,
        ...(dodEnabled && dodData?.length > 0 ? { dodData } : {}),
      }),
    [chartData, metricKey, dodEnabled, dodData],
  );

  React.useEffect(() => {
    if (instance && chartOption) {
      instance.setOption(chartOption, { notMerge: true });
    }
  }, [instance, chartOption]);

  return (
    <SparklineWrapper>
      <BaseChart
        chartId={`network-trend-${metricKey}`}
        chartInstanceRefCb={chartInstanceRefCb}
        style={{ height: '100%', width: '100%' }}
      />
    </SparklineWrapper>
  );
};

const calcChange = (current: number | null, previous: number | null): number | null => {
  if (current === null || previous === null || previous === 0) return null;
  return ((current - previous) / previous) * 100;
};

const formatChangePct = (change: number | null): string => {
  if (change === null) return '--';
  if (change === 0) return '0%';
  return `${change > 0 ? '+' : ''}${change.toFixed(1)}%`;
};

interface DodBadgeProps {
  change: number | null;
  // For network metrics higher is bad, so increase = red
  reverseColor?: boolean;
}

const DodBadge = ({ change, reverseColor = true }: DodBadgeProps) => {
  const isNeutral = change === null || change === 0;
  // reverseColor=true: increase is bad (red), decrease is good (green)
  const isPositive = isNeutral ? undefined : reverseColor ? change! < 0 : change! > 0;

  return (
    <TrendValue $positive={isPositive} $neutral={isNeutral}>
      {!isNeutral && (change! > 0 ? <RiseOutlined /> : <FallOutlined />)}
      {formatChangePct(change)}
    </TrendValue>
  );
};

interface RowComponentProps {
  metricKey: string;
  data: any;
  dodData: any;
}

export const RowComponent = ({ metricKey, data, dodData }: RowComponentProps) => {
  const dodEnabled = useAtomValue(enableDodAtom);
  const percentageMetricKey = getPercentageMetricByKey({ metricKey, defaultValue: null });

  // Current period totals
  const countData: number | null = get(data, ['totals', metricKey], null);
  const percentageData: number | null = percentageMetricKey
    ? get(data, ['totals', percentageMetricKey], null)
    : null;

  // DoD period totals
  const countDodData: number | null = dodEnabled ? get(dodData, ['totals', metricKey], null) : null;
  const percentageDodData: number | null =
    dodEnabled && percentageMetricKey ? get(dodData, ['totals', percentageMetricKey], null) : null;

  const countChange = useMemo(() => calcChange(countData, countDodData), [countData, countDodData]);
  const percentageChange = useMemo(
    () => calcChange(percentageData, percentageDodData),
    [percentageData, percentageDodData],
  );

  const countHumanized = humanizeUtil(countData as number, metricKey);
  const percentageHumanized = percentageMetricKey
    ? humanizeUtil(percentageData as number, percentageMetricKey)
    : null;

  const displayName = getMetricDisplayName({ metricKey, defaultValue: metricKey });

  const pctKey = percentageMetricKey ?? metricKey;
  const pctChartData: any[] = get(data, ['data', pctKey], get(data, ['data', metricKey], []));
  const pctDodChartData: any[] = get(
    dodData,
    ['data', pctKey],
    get(dodData, ['data', metricKey], []),
  );

  const fmtCount =
    countData !== null
      ? `${countHumanized.humanizedNumber}${countHumanized.humanizedUnit ? ` ${countHumanized.humanizedUnit}` : ''}`
      : '--';

  const fmtPct =
    percentageHumanized && percentageData !== null
      ? `${percentageHumanized.humanizedNumber}${percentageHumanized.humanizedUnit ? ` ${percentageHumanized.humanizedUnit}` : ''}`
      : null;

  return (
    <Row>
      <FullCell title={displayName}>{displayName}</FullCell>

      {/* Count column: value | DoD badge */}
      <Cell>
        <CellNumber>
          <CellPrimary>{fmtCount}</CellPrimary>
        </CellNumber>
        {dodEnabled && <DodBadge change={countChange} />}
      </Cell>

      {/* Percentage column: value | DoD badge | sparkline */}
      <Cell $flex={2}>
        <CellWrapper $dodEnabled={dodEnabled}>
          <CellNumber>
            <CellPrimary>{fmtPct ?? fmtCount}</CellPrimary>
          </CellNumber>
          {dodEnabled && percentageHumanized && <DodBadge change={percentageChange} />}
        </CellWrapper>
        <Sparkline metricKey={pctKey} chartData={pctChartData} dodData={pctDodChartData} />
      </Cell>
    </Row>
  );
};
